import { useMemo } from "react";
import {
  RoadmapItemDto,
  ResponseRecommendation,
  RoadmapItemSourceType,
} from "../../../../../customer-experience/src/lib/ShiOneClient";

/**
 * Hook to extract recommendation data from a roadmap item's snapshot
 * This avoids additional API calls since we already have all the data
 */
const useRecommendationFromSnapshot = (roadmapItem: RoadmapItemDto | null) => {
  const recommendation = useMemo(() => {
    if (!roadmapItem) return null;

    // Try to parse the recommendation snapshot if it exists
    if (roadmapItem.recommendationSnapshot) {
      try {
        const snapshotData = JSON.parse(roadmapItem.recommendationSnapshot);

        // Handle Assessment recommendations
        if (
          roadmapItem.sourceType ===
          RoadmapItemSourceType.AssessmentRecommendation
        ) {
          // The snapshot contains the full recommendation data
          if (snapshotData.ExtendedData?.OriginalMetadata) {
            // Parse the nested OriginalMetadata JSON
            const originalMetadata = JSON.parse(
              snapshotData.ExtendedData.OriginalMetadata,
            );
            const responseRec = originalMetadata.ResponseRecommendation;

            if (responseRec) {
              // Create a ResponseRecommendation from the snapshot data
              return new ResponseRecommendation({
                recommendationId:
                  responseRec.RecommendationId ||
                  snapshotData.SourceRecordId?.split(":")[1],
                assessmentId:
                  responseRec.AssessmentId || snapshotData.ResourceIdentifier,
                capability: responseRec.Capability || snapshotData.Title,
                recommendationsSummary:
                  responseRec.RecommendationsSummary ||
                  snapshotData.Description,
                recommendations: responseRec.Recommendations,
                impact: responseRec.Impact,
                category: responseRec.Category || snapshotData.Category,
                subcategory: responseRec.Subcategory,
                program: responseRec.Program,
                knownTools: responseRec.KnownTools || [],
                knownToolsLogos: responseRec.KnownToolsLogos || [],
                otherTools: responseRec.OtherTools || [],
                otherToolsLogos: responseRec.OtherToolsLogos || [],
              });
            }
          }
        }

        // For Azure recommendations, the snapshot structure is different
        // but they're handled directly in AzureRecommendationDetailsDialog
      } catch (error) {
        console.error("Error parsing recommendation snapshot:", error);
      }
    }

    // Fallback: construct from roadmap item data if no snapshot
    return new ResponseRecommendation({
      recommendationId:
        roadmapItem.recommendationId ||
        roadmapItem.sourceMetadata?.recommendationId,
      assessmentId: roadmapItem.sourceMetadata?.assessmentId,
      capability: roadmapItem.name,
      recommendationsSummary: roadmapItem.description,
      recommendations: roadmapItem.description,
      impact: roadmapItem.sourceMetadata?.impact || "Medium",
      category: roadmapItem.sourceMetadata?.category,
      subcategory: roadmapItem.sourceMetadata?.subcategory,
      program: roadmapItem.sourceMetadata?.program,
      knownTools: [],
      knownToolsLogos: [],
      otherTools: [],
      otherToolsLogos: [],
    });
  }, [roadmapItem]);

  return {
    data: recommendation,
    isLoading: false,
    isError: false,
  };
};

export default useRecommendationFromSnapshot;
